<template>
    <div>
        <EditKrpano
            v-if="canBeEditable && data"
            :krpanoHotspot="data.krpanoHotspot"
            :sceneKey="roomId"
            :type="data.type"
            :widgetTitle="data.widgetTitle"
            :editProperties="editProperties"
            :defaultProperties="defaultProperties"
            :loaded="widgetLoaded"
            :updateJsonUrl="updateJsonUrl"
            @set-camera="setCamera"
            type="krpano"
        />
        <MyHabiteoStudio
            v-if="isExportMediaEnabled && formUrl"
            :formUrl="formUrl"
            :zoomFactor="zoomFactor"
            :exportedBlobFile="exportedBlobFile"
            :programName="data.program.name"
            :widgetName="data.widgetTitle"
            :isAProgram="false"
            :defaultQuality="defaultQuality"
            :defaultsPresetValues="defaultsPresetValues"
            @export-media="exportMedia"
            @set-aspect-ratio="setAspectRatio"
            @set-mention="setMention"
            @set-quality="setQuality"
            @set-preset="setPreset"
            @set-advanced_preset="setAdvancedPreset"
        />
        <LoaderProgressBar v-if="isLoadingScene"/>
        <div
            id="tourModels"
            ref="confTourModelsHolder"
            class="content-widget conf-tour-models"
        >
            <div 
                v-if="isLoading"
                class="d-flex vv-conf-navbar"
            >
                <v-skeleton-loader type="chip" />
                <v-skeleton-loader class="pl-8" type="chip" />
                <v-skeleton-loader class="pl-5" type="chip" />
            </div>
            <div 
                v-else
                class="d-flex vv-conf-navbar" 
                :style="[isOpenedSidebar ? {left: '370px'}: {justifyContent: 'center'}]"
            >
                <ConfigurableTourHeader
                    :data="data"
                    :isConfModels="true"
                />
                <RoomItemsFooter 
                    :data="data"
                    :isConfModels="true"/>
            </div>
        </div>
        <FunnelFooter v-if="hasFunnel"/>
    </div>
</template>

<script>
    import { EventBus } from '@/event-bus';
    import Http from '@/api/data';
    import { mapActions, mapGetters } from 'vuex';
    import RoomItemsFooter from '@/layout/alternative/configurableTour/components/RoomsItemsFooter';
    import ConfigurableTourHeader from '@/layout/alternative/configurableTour/ConfigurableTourHeader';
    import Vue from 'vue';

    import EditKrpano from "@/components/editMode/EditKrpano";
    import MyHabiteoStudio from "@/components/myHabiteoStudio/MyHabiteoStudio";
    import FunnelFooter from '@/components/funnel/FunnelFooter.vue';
    import LoaderProgressBar from '@/components/LoaderProgressBar';

    export default {
        name: 'ConfTourModels',
        props: [
            'data',
            'link',
            'active',
            'standalone',
            'isOpenedSidebar'
        ],
        components: {
            EditKrpano,
            MyHabiteoStudio,
            FunnelFooter,
            LoaderProgressBar,
            RoomItemsFooter,
            ConfigurableTourHeader
        },
        data: () => ({
            krpanoWidget: null,
            backgroundSources: null,
            combinationSources: {},
            canvas: null,
            canvasData: {},
            context: null,
            activatedRoom: 0,
            roomItemIndex: 0,
            isLoadingScene: false,
            editProperties: {
                'viewAngle': null,
                'zoomFactor': null,
                'north': null
            },
            defaultProperties: {
                'viewAngle': 0,
                'zoomFactor': 0,
                'north': null
            },
            mouseTimeout: null,
            widgetLoaded: false,
            skippedMaterialTypes: [
                'earthenware',
                'flooring-carpentry',
                'floor',
                'furniture',
                'wall'
            ],
            camera: null,
            formUrl: null,
            exportedBlobFile: null,
            defaultsPresetValues: 'preset_natural_angle',
            items: [],
            previousRoom: null

        }),
        computed: {
            ...mapGetters([
                'isOfflineMode',
                'isReviewUse',
                'language',
                'protocol',
                'isLoading',
                'isIframe'
            ]),
            ...mapGetters('widget', [
                'funnel'
            ]),
            apiUrl() {
                return this.protocol + (this.isReviewUse ? process.env.API_LIST.REVIEW : process.env.API_LIST.FO);
            },
            isEditable() {
                return this.isInternal ? this.$route.query.edit !== undefined : false;
            },
            canBeEditable() {
                if (this.standalone && this.isEditable)
                    return true;
                return false;
            },
            isExportMediaEnabled() {
                return this.standalone && this.$route.query.mediaExport !== undefined;
            },
            updateJsonUrl() {
                return this.$props.data.private ? this.$props.data.private.jsonURI : null;
            },
            isStudioEnabled() {
                return this.isExportMediaEnabled && this.formUrl ? true : false;
            },
            zoomFactor() {
                return this.editProperties && this.editProperties.zoomFactor  ? this.editProperties.zoomFactor : 0;
            },
            roomId() {
                return this.$props.data.configurationOptions ? this.$props.data.configurationOptions.find(option => option.active).id : null;
            },
            apiUrl() {
                return this.protocol + (this.isReviewUse ? process.env.API_LIST.REVIEW : process.env.API_LIST.FO);
            },
            hasFunnel() {
                return this.funnel && !this.isFunnel;
            },
            isFunnel() {
                return this.isIframe && document.referrer === process.env.FUNNEL_URL;
            },
            defaultQuality() {
                return this.krpanoWidget && this.krpanoWidget.data && this.krpanoWidget.data.resolution4k ? '4k' : 'fullhd';
            }
        },
        async mounted() {
            this.isIE = window.navigator.msPointerEnabled;
            this.canvas = document.createElement('canvas');
            this.context = this.canvas.getContext('2d');
            this.loadData();

            EventBus.$on('loadRoom', roomIndex => {
                if (!this.isLoadingScene) {
                    this.isLoadingScene = true;
                    this.loadScene(roomIndex);
                }
            });

            EventBus.$on('updateMaterials', (roomItem, material) => {
                if (!this.isLoadingScene) {
                    console.log('Applying %s (%s) to room item "%s"', material.id, material.name, roomItem.name);
                    this.data.json.items[roomItem.id] = material.id;
                    this.canvasData.scenes[this.activatedRoom].active = true;
                    this.roomItemIndex = this.data.configurationOptions[this.activatedRoom].items.findIndex(item => item.id === roomItem.id);
                    this.updateRoomItems();
                }
            });

            EventBus.$on('applyHarmony', (harmonyRoomItem, roomItemToUpdate) => {
                if (!this.isLoadingScene) {
                    console.log('Apply harmony "%s"', harmonyRoomItem.name);
                    roomItemToUpdate.forEach(roomItem => {
                        let materialToApply = harmonyRoomItem.materials.filter(material => material.active);
                        if (materialToApply.length) {
                            for (const [key, value] of Object.entries(materialToApply[0].combinations)) {
                                this.data.json.items[key] = value;
                            }
                        }
                    })
                    if (harmonyRoomItem.camera){
                        this.roomItemIndex = this.data.configurationOptions[this.activatedRoom].items.findIndex(item => item.id === harmonyRoomItem.id);
                    }
                    this.updateRoomItems(harmonyRoomItem.camera);
                }
                });
        },
        methods: {
            ...mapActions([
                'setIsLoading'
            ]),
            
            loadData() {
                let urlLink = this.$props.link ? this.$props.link : this.apiUrl + '/request-data/conf-tour-models' + window.location.search;
                if (this.language && !urlLink.includes('&language'))
                    urlLink += '&language=' + this.language;
                if (this.isOfflineMode && !urlLink.includes('&offline'))
                    urlLink += '&offline';
                Http.getData(urlLink).then(payload => {
                    if (payload) {
                        payload.type = 'conf-models';
                        if (payload.language)
                            this.$i18n.locale = payload.language;
                        this.items = payload.items;
                        if (payload.items[0]) {
                            this.loadVVConf(payload.items[0].link);
                        }
                    }
                });
            },
            loadVVConf(urlLink) {
                Http.getData(urlLink).then(payload => {
                    payload.items = this.items;
                    Http.getData(payload.private.jsonURI).then(json => {
                            let roomList = json.rooms;
                            let materialList = json.materials;
                            let configurationOptions = [];
                            let isFirstRoom = true;
                            roomList.forEach(room => {
                                let currentRoom = {
                                    'id': room.id,
                                    'name': room.name,
                                    'type': room.type,
                                    'active': isFirstRoom,
                                    'hasCombination': Object.keys(room.media.combinations).length > 0 ? true : false
                                };
                                isFirstRoom = false;
                                let roomItemList = [];
                                room.items.forEach(roomItem => {
                                    let roomMaterial = [];
                                    for (let materialIndex = 0; materialIndex < roomItem.materials.length; materialIndex++) {
                                        let material = {};
                                        if (roomItem.type === 'harmony') {
                                            material['combinations'] = roomItem.materials[materialIndex].combinations;
                                            material['name'] = roomItem.materials[materialIndex].name || null;
                                            material['active'] = false;
                                        } else {
                                            let materialId = roomItem.materials[materialIndex];
                                            material = materialList[materialId] || {};
                                            // START if BO doesn't VV materials in Promoter's catalog
                                            if (!material['id']){
                                                material['id'] = materialId;
                                                material['name'] = materialList[materialId];
                                            }
                                            if (!material['img']){
                                                let img = '';
                                                let materialItems = materialId.split(/-(.*)/, 2);
                                                let isPrefab = materialItems.length > 1;
                                                if (isPrefab)
                                                    img = process.env.PREFAB_PREVIEW + materialItems[0] + '/' + materialItems[1];
                                                else
                                                    img = process.env.MATERIAL_PREVIEW + materialItems[0];
                                                material['img'] = img;
                                            }
                                            // END
                                            material['active'] = materialIndex === roomItem.defaultMaterial || false;
                                        }
                                        roomMaterial.push(material);
                                    }
                                    roomItemList.push({
                                        'id': roomItem.id,
                                        'name': roomItem.name,
                                        'description': roomItem.description || null,
                                        'type': roomItem.type,
                                        'materials': roomMaterial,
                                        'visible': roomItem.isActive,
                                        'camera': roomItem.camera
                                    });
                                });
                                currentRoom.items = roomItemList;
                                configurationOptions.push(currentRoom);
                            });

                            if (this.isExportMediaEnabled && (payload.private && 'studioExport' in payload.private))
                                this.formUrl = payload.private.studioExport;

                            payload.type = 'conf-models';
                            payload.widgetType = 'conf-models';
                            payload.json = json;

                            payload.materials = json.materials;
                            payload.configurationOptions = configurationOptions;
                            if (payload.gui && payload.gui.autorotate && payload.gui.autorotate.active) {
                                payload.gui.autorotate.active = !(this.canBeEditable || this.isExportMediaEnabled);
                                payload.gui.autorotate.visible = !this.canBeEditable;
                            }
                            if (payload.gui)
                                EventBus.$emit('updateSidebar', Object.assign(this.$props.data, { 'gui': payload.gui }), false);

                            json.name = payload.widgetTitle;
                            json.disclaimer = payload.disclaimer || undefined;
                            json.showCopyright = payload.showCopyright !== undefined ? payload.showCopyright : true;

                            //Remove plot title
                            payload.title = '';
                            payload.subtitle = '';

                            if (this.standalone)
                                EventBus.$emit('updateLoadedWidgets', {
                                    'id': payload.type,
                                    'link': urlLink,
                                    'active': true,
                                    'widgetType': payload.widgetType
                                });
                            EventBus.$emit('updateSidebar', payload, false);

                            this.initPano(json);
                        });

                });

            },
            
            triggerResize() {
                if (this.isIE) {
                    let evt = window.document.createEvent('UIEvents');
                    evt.initUIEvent('resize', true, false, window, 0);
                    window.dispatchEvent(evt);
                } else
                    window.dispatchEvent(new Event('resize'));
            },
            getKrpanoConfig(apiUrl, callback) {
                Http.getData(apiUrl).then(payload => {
                    callback(payload);
                }).catch(error => {
                    // this.errorState = true;
                    console.error('Error :', error);
                    callback(null);
                });
            },
            
            loadScene(newRoom) {
                console.log('Loading "%s" room', this.data.json.rooms[newRoom].name);
                // Check for configurable room.
                if (!this.data.json.rooms[newRoom]) {
                    const result = this.krpanoWidget.loadScene(newRoom);
                    this.isLoadingScene = false;
                }

                // Reset cache.
                this.backgroundSources = null;
                this.combinationSources = {};

                this.data.configurationOptions[newRoom].items.forEach(configRoomItem => {
                    let activatedMaterial = configRoomItem.materials.filter(material => material.active);
                    let activatedMaterialId;
                    if (activatedMaterial.length)
                        activatedMaterialId = activatedMaterial[0].id;
                    else {
                        // If no default material, use the first one
                        if (configRoomItem.materials.length > 0) {
                            activatedMaterialId = configRoomItem.materials[0].id;
                            configRoomItem.materials[0].active = true;
                        }
                    }

                    this.data.json.items[configRoomItem.id] = activatedMaterialId;
                });

                // Update materials and scene.
                this.applyMaterials(newRoom, this.data.json, () => {
                    this.isLoadingScene = false;
                });
            },
            // Apply materials for the specified room.
            applyMaterials(roomIndex, json, callback) {
                // Texture faces.
                let faces = [ 'l', 'f', 'r', 'b', 'u', 'd' ];

                // Get matching room.
                let room = json.rooms[roomIndex];

                // Cache mask and material images.
                let sources = [];
                for (let itemId in room.media.combinations) {
                    if (!room.media.combinations.hasOwnProperty(itemId))
                        continue;

                    // Check for combination.
                    let combination = room.media.combinations[itemId];
                    if (!combination)
                        return;

                    // Cache mask images if any.
                    let combinationSource = this.combinationSources[itemId];
                    if (!combinationSource) {
                        combinationSource = { masks: {} };
                        faces.forEach(face =>{
                            let url = combination.masks[face];
                            if (!url)
                                return;
                            let source = { url: url };
                            sources.push(source);
                            combinationSource.masks[face] = source;
                        });
                        this.combinationSources[itemId] = combinationSource;
                    }

                    // Cache new material images.
                    let material = json.items[itemId];
                    if (material != combinationSource.material) {
                        combinationSource.bufferCanvas = null; // Reset cache.
                        combinationSource.material = material;
                        combinationSource.materials = {};
                        faces.forEach(face => {
                            let url = combination.materials[material][face];
                            if (!url)
                                return;
                            let source = { url: url };
                            sources.push(source);
                            combinationSource.materials[face] = source;
                        });
                    }
                }

                // Add backgrounds.
                if (!this.backgroundSources) {
                    this.backgroundSources = {};
                    faces.forEach(face => {
                        let url = room.media.background[face];
                        if (!url)
                            return;
                        let source = { url: url };
                        sources.push(source);
                        this.backgroundSources[face] = source;
                    });
                }

                // Load sources.
                this.loadImages(sources, () => {
                    console.time('iteration');

                    // Setup canvases.
                    let sourceIndex = 0;
                    let source = sources[sourceIndex];
                    let image = source.image;
                    let width = image.width;
                    let height = image.height;
                    this.canvas.width = width;
                    this.canvas.height = height * faces.length;

                    // Draw masks as transparent images.
                    for (let itemId in this.combinationSources) {
                        if (!this.combinationSources.hasOwnProperty(itemId))
                            continue;

                        // Check for cache.
                        let combinationSource = this.combinationSources[itemId];
                        if (!combinationSource.bufferCanvas) {
                            // Create buffer canvas.
                            let bufferCanvas = document.createElement('canvas');
                            bufferCanvas.width = width;
                            bufferCanvas.height = height * faces.length;
                            let bufferContext = bufferCanvas.getContext('2d');
                            bufferContext.fillStyle = 'rgb(255, 255, 255)';
                            bufferContext.clearRect(0, 0, width, height * 6);

                            // Check for cache.
                            if (!combinationSource.imageData) {
                                // Draw images.
                                faces.forEach(function (face, faceIndex) {
                                    let source = combinationSource.masks[face];
                                    if (!source)
                                        bufferContext.fillRect(0, height * faceIndex, width, height);
                                    else {
                                        image = source.image;
                                        image.crossOrigin = "Anonymous";
                                        bufferContext.drawImage(image, 0, height * faceIndex);
                                    }
                                });

                                // Extract alpha channel.
                                let imageData = bufferContext.getImageData(0, 0, this.canvas.width, this.canvas.height);
                                for (let i = 0; i < imageData.data.length; i +=4) {
                                    let alpha = imageData.data[i];
                                    imageData.data[i + 3] = 255 - alpha;
                                }

                                // Extract alpha channel.
                                var offset = 0;
                                combinationSource.massCenters = {};
                                faces.forEach(function (face, faceIndex) {
                                    var centerX = 0;
                                    var centerY = 0;
                                    var weight = 0;
                                    for (var y = 0; y < height; y++)
                                        for (var x = 0; x < width; x++) {
                                            var alpha = imageData.data[offset];
                                            imageData.data[offset + 3] = 255 - alpha;
                                            offset += 4;
                                            if (alpha) {
                                                centerX += x;
                                                centerY += y;
                                                weight++;
                                            }
                                        }

                                    if (combinationSource.masks[face])
                                        combinationSource.massCenters[face] = {
                                            'x': weight ? (centerX / weight) | 0 : 0,
                                            'y': weight ? (centerY / weight) | 0 : 0,
                                            'weight': weight
                                        };
                                });

                                // Persist image data in cache.
                                combinationSource.imageData = imageData;
                            }
                            bufferContext.putImageData(combinationSource.imageData, 0, 0);

                            // Merge material textures.
                            bufferContext.globalCompositeOperation = 'xor';
                            faces.forEach(function (face, faceIndex) {
                                let source = combinationSource.materials[face];
                                if (source) {
                                    image = source.image;
                                    bufferContext.drawImage(image, 0, height * faceIndex);
                                }
                            });

                            // Persist canvas in cache.
                            combinationSource.bufferCanvas = bufferCanvas;
                        }
                        this.context.drawImage(combinationSource.bufferCanvas, 0, 0);
                    }

                    // Draw background.
                    this.context.globalCompositeOperation = 'destination-over';
                    faces.forEach((face, faceIndex) => {
                        source = this.backgroundSources[face];
                        if (source) {
                            image = source.image;
                            image.crossOrigin = "Anonymous";
                            this.context.drawImage(image, 0, height * faceIndex);
                        }
                    });
                    var activeRoomIndex = this.data.configurationOptions.findIndex(config => config.active);
                    var roomType = this.data.configurationOptions[activeRoomIndex].type;
                    var roomItem = this.data.configurationOptions[activeRoomIndex].items[this.roomItemIndex];
                    if (roomItem) {
                        var faceCounter = this.combinationSources[roomItem.id] ? Object.keys(this.combinationSources[roomItem.id].masks).length : 0;
                        if (!roomItem.camera && (faceCounter < 4 || (faceCounter >= 4 && !this.skippedMaterialTypes.includes(roomType))))
                            this.camera = roomItem.camera = this.computeItemCamera(roomItem.id, this.data.json.rooms[activeRoomIndex]);
                        else {
                            if (roomItem.camera)
                                this.camera = roomItem.camera;
                            else
                                console.log('camera skipped : camera already moved %s or there are more than 4 faces (face counter: %s)', roomItem.camera, faceCounter);
                        }
                    }
                    console.timeEnd('iteration');

                    console.time('blob');
                    this.canvas.toBlob((blob) => {
                        this.canvasData.scenes[roomIndex].regexp = URL.createObjectURL(blob);
                        this.canvasData.isStudioEnabled = this.isStudioEnabled;
                        this.canvasData.isInternal = this.isInternal;
                        console.timeEnd('blob');
                        if (!this.krpanoWidget)
                            this.krpanoWidget = new Habiteo.PanoWidget(this.$refs.confTourModelsHolder , this.canvasData, document.getElementById('app'));
                        else {
                            if (this.previousRoom && this.previousRoom.id != room.id || this.previousRoom == null) 
                                this.krpanoWidget.updateScene(roomIndex, this.canvasData);
                            
                            this.previousRoom = room;
                        }
                            
                        callback && callback();
                    }, 'image/jpeg');
                });
            },
            // Loads the specified images then callbacks.
            loadImages(sources, callback) {
                // Load sources.
                let count = 0;
                sources.forEach( (source, index) => {
                    // Ignore already loaded images.
                    if (source.image) {
                        count++;
                        if (count == sources.length)
                            callback();
                        return;
                    }

                    // Load new image.
                    let image = new Image;
                    image.onload = () => {
                        count++;
                        if (count == sources.length)
                            callback();
                    };
                    image.src = source.url;
                    image.crossOrigin = 'Anonymous';
                    source.image = image;
                });
            },
            initPano(jsonData) {
                // Generate scenes.
                let scenes = [];
                jsonData.rooms.forEach((room, roomIndex) => {
                    scenes.push({
                        'key': ('' + (roomIndex)).padStart(4, '0'),
                        'id': ('' + (roomIndex)).padStart(4, '0'),
                        'name': room.name,
                        'path': '',
                        'sceneSuffix': '',
                        'stereo': false,
                        'multires': false,
                        'regexp': '',
                        'preview': false,
                        'camera': room.camera,
                        'panoType': 'cubestrip'
                    });
                });
                this.activatedRoom = 0;

                let vm = this;
                this.canvasData = {
                    scenes: scenes,
                    name: jsonData.name,
                    showThumbnails: false,
                    disclaimer: jsonData.disclaimer,
                    showCopyright: jsonData.showCopyright,
                    gui: this.data.gui,
                    callbacks: {
                        // Callback when krpano is ready.
                        initCallback: () => {
                            EventBus.$emit('sceneLoaded');
                        },
                        // Callback when new scene is loaded.
                        sceneLoadedCallback: (scene, sceneIndex) => {
                            vm.widgetLoaded = true;
                            let camera = scene.camera || [0, 0, 0];

                            vm.defaultProperties.viewAngle = [camera[0], camera[1]];
                            vm.defaultProperties.north = camera[0];
                            vm.defaultProperties.zoomFactor = camera[2];

                            vm.setIsLoading(false);
                            
                            //Check for scene change.
                            if (sceneIndex == this.activatedRoom)
                                return;

                            var data = Object.assign(vm.data, { 'sceneKey': scene.id });
                            EventBus.$emit('updateSidebar', data, false);
                            this.activatedRoom = sceneIndex;

                            // Check for configurable room.
                            if (!jsonData.rooms[this.activatedRoom]) {
                                app.opened = false;
                                return;
                            }
                        },
                        // Callback when new scene is requested.
                        sceneRequestCallback: (scene, sceneIndex) => {
                            EventBus.$emit('switchRoom', sceneIndex);

                            // Reset default properties.
                            vm.defaultProperties.viewAngle = [scene.camera[0], scene.camera[1]];
                            vm.defaultProperties.north = scene.camera[0];
                            vm.defaultProperties.zoomFactor = scene.camera[2];
                        },
                        viewChangeCallback(camera, index) {
                            // Setup mouse handler.
                            vm.mouseTimeout && clearTimeout(vm.mouseTimeout);
                            vm.mouseTimeout = setTimeout(() => {
                                vm.editProperties.viewAngle = [camera[0], camera[1]];
                                vm.editProperties.north = camera[0];
                                vm.editProperties.zoomFactor = camera[2];
                            }, 1000);
                        },
                        exportMediaCallback(blob) {
                            vm.exportedBlobFile = blob;
                        },
                        widgetReadyCallback(widget,data) {
                            if (vm.$props.data.gui)
                                data.gui = vm.$props.data.gui;

                            EventBus.$emit('trackWidget', payload.tracking);
                            vm.setIsLoading(false);
                        }
                    },
                    dependencies: {
                        vue: Vue
                    }
                };

                // Apply default materials.
                jsonData.items = {};
                jsonData.rooms.forEach(room => {
                    room.items.forEach(item => {
                        jsonData.items[item.id] = item.materials[item.defaultMaterial];
                    });
                });
                this.canvasData.scenes[0].active = true;
    
                if (this.standalone)
                    this.applyMaterials(0, jsonData);

                vm.data.sceneKey = this.canvasData.scenes[0].key;
                vm.data.type = 'conf-models';
                vm.data.widgetType = 'conf-models';
                EventBus.$emit('updateSidebar', vm.data, false);
            },
            computeItemCamera(itemId, room) {
                // Check for matching combination.
                var combination = room.media.combinations[itemId];
                if (!combination)
                    return null;

                var combinationSource = this.combinationSources[itemId];
                if (!combinationSource)
                    return null;

                // Determine weighted center of all faces.
                var ath = 0, atv = 0, weight = 0;
                var massCenters = combinationSource.massCenters;
                var massCenter = massCenters['l'];
                if (massCenter) {
                    var X = massCenter.x / combinationSource.imageData.width - 0.5;
                    var Z = 0.5 - massCenter.y / 1024;
                    var Y = -0.5;
                    var R = Math.sqrt(X * X + Y * Y + Z * Z);
                    var centerX = Math.atan2(Y, X) / (2 * Math.PI) * 360;
                    var centerY = Math.acos(Z / R) / Math.PI * 180 - 90;
                    ath += centerX * massCenter.weight;
                    atv += centerY * massCenter.weight;
                    weight += massCenter.weight;
                }
                var massCenter = massCenters['f'];
                if (massCenter) {
                    var Y = massCenter.x / combinationSource.imageData.width - 0.5;
                    var Z = 0.5 - massCenter.y / combinationSource.imageData.width;
                    var X = 0.5;
                    var R = Math.sqrt(X * X + Y * Y + Z * Z);
                    var centerX = Math.atan2(Y, X) / (2 * Math.PI) * 360;
                    var centerY = Math.acos(Z / R) / Math.PI * 180 - 90;
                    ath += centerX * massCenter.weight;
                    atv += centerY * massCenter.weight;
                    weight += massCenter.weight;
                }
                var massCenter = massCenters['r'];
                if (massCenter) {
                    var X = 0.5 - massCenter.x / combinationSource.imageData.width;
                    var Z = 0.5 - massCenter.y / combinationSource.imageData.width;
                    var Y = 0.5;
                    var R = Math.sqrt(X * X + Y * Y + Z * Z);
                    var centerX = Math.atan2(Y, X) / (2 * Math.PI) * 360;
                    var centerY = Math.acos(Z / R) / Math.PI * 180 - 90;
                    ath += centerX * massCenter.weight;
                    atv += centerY * massCenter.weight;
                    weight += massCenter.weight;
                }
                var massCenter = massCenters['b'];
                if (massCenter) {
                    var Y = 0.5 - massCenter.x / combinationSource.imageData.width;
                    var Z = 0.5 - massCenter.y / combinationSource.imageData.width;
                    var X = -0.5;
                    var R = Math.sqrt(X * X + Y * Y + Z * Z);
                    var centerX = Math.atan2(Y, X) / (2 * Math.PI) * 360;
                    var centerY = Math.acos(Z / R) / Math.PI * 180 - 90;
                    ath += centerX * massCenter.weight;
                    atv += centerY * massCenter.weight;
                    weight += massCenter.weight;
                }
                var massCenter = massCenters['u'];
                if (massCenter) {
                    var Y = massCenter.x / combinationSource.imageData.width - 0.5;
                    var X = massCenter.y / combinationSource.imageData.width - 0.5;
                    var Z = 0.5;
                    var R = Math.sqrt(X * X + Y * Y + Z * Z);
                    var centerX = Math.atan2(Y, X) / (2 * Math.PI) * 360;
                    var centerY = Math.acos(Z / R) / Math.PI * 180 - 90;
                    ath += centerX * massCenter.weight;
                    atv += centerY * massCenter.weight;
                    weight += massCenter.weight;
                }
                var massCenter = massCenters['d'];
                if (massCenter) {
                    var Y = massCenter.x / combinationSource.imageData.width - 0.5;
                    var X = 0.5 - massCenter.y / combinationSource.imageData.width;
                    var Z = -0.5;
                    var R = Math.sqrt(X * X + Y * Y + Z * Z);
                    var centerX = Math.atan2(Y, X) / (2 * Math.PI) * 360;
                    var centerY = Math.acos(Z / R) / Math.PI * 180 - 90;
                    ath += centerX * massCenter.weight;
                    atv += centerY * massCenter.weight;
                    weight += massCenter.weight;
                }

                // Get average.
                ath /= weight;
                atv /= weight;
                var fov = (room.camera || [0, 0, 110])[2]; // Reuse room FOV.
                return [ ath, atv, fov ];
            },
            updateRoomItems() {
                this.applyMaterials(this.activatedRoom, this.data.json, () => {
                    this.krpanoWidget.applyMaterials(this.activatedRoom, this.camera);
                    this.camera = null;
                });
            },
            exportMedia(settings) {
                this.krpanoWidget.shotPano(settings);
            },
            setAspectRatio(ratio) {
                this.krpanoWidget.data.aspectRatio = ratio;
            },
            setPreset(preset) {
                this.krpanoWidget.data.preset = preset;
            },
            setAdvancedPreset(advancedPreset){
                this.krpanoWidget.data.preset = advancedPreset;
            },
            setMention(option) {
                this.krpanoWidget.data.mention = option;
            },
            setQuality(quality) {
                this.krpanoWidget.data.exportQuality = quality;
            },
            setCamera(data) {
                if (data.metadata) {
                    let camera;
                    let currentScene = this.krpanoWidget.data.scenes.find(scene => scene.id === data.key);
                    if (data.metadata['viewAngle'] && data.metadata['zoomFactor'])
                        camera = data.metadata['viewAngle'].concat(data.metadata['zoomFactor']);
                    else if (data.metadata['viewAngle'])
                        camera = data.metadata['viewAngle'].concat([currentScene.camera[2]]);
                    else
                        camera = [currentScene.camera[0], currentScene.camera[1]].concat(data.metadata['zoomFactor']);
                    this.krpanoWidget.widget.setCamera(data.key, camera);
                }
            },
            triggerAnimation(element) {
                element.classList.remove('animated-content');
                void element.offsetWidth;
                element.classList.add('animated-content');
            },
            async updateData() {
                let urlLink = this.$props.link ? this.$props.link : this.apiUrl + '/request-data/configurable-tour' + window.location.search;
                if (this.language && !urlLink.includes('&language'))
                    urlLink += '&language=' + this.language;

                Http.getData(urlLink).then(payload => {
                    if (payload) {
                        Http.getData(payload.private.jsonURI).then(async json => {
                            payload.items = this.items;
                            let roomList = json.rooms;
                            let materialList = json.materials;
                            let configurationOptions = [];
                            let isFirstRoom = true;
                            roomList.forEach(room => {
                                let currentRoom = {
                                    'id': room.id,
                                    'name': room.name,
                                    'type': room.type,
                                    'active': isFirstRoom,
                                    'hasCombination': Object.keys(room.media.combinations).length > 0 ? true : false
                                };
                                isFirstRoom = false;
                                let roomItemList = [];
                                room.items.forEach(roomItem => {
                                    if (!roomItem.isActive)
                                        return;
                                    let roomMaterial = [];
                                    for (let materialIndex = 0; materialIndex < roomItem.materials.length; materialIndex++) {
                                        let material = {};
                                        if (roomItem.type === 'harmony') {
                                            material['combinations'] = roomItem.materials[materialIndex].combinations;
                                            material['name'] = roomItem.materials[materialIndex].name || null;
                                            material['active'] = false;
                                        } else {
                                            let materialId = roomItem.materials[materialIndex];
                                            material = materialList[materialId] || {};
                                            // START if BO doesn't VV materials in Promoter's catalog
                                            if (!material['id']){
                                                material['id'] = materialId;
                                                material['name'] = materialList[materialId];
                                            }
                                            if (!material['img']){
                                                let img = '';
                                                let materialItems = materialId.split(/-(.*)/, 2);
                                                let isPrefab = materialItems.length > 1;
                                                if (isPrefab)
                                                    img = process.env.PREFAB_PREVIEW + materialItems[0] + '/' + materialItems[1];
                                                else
                                                    img = process.env.MATERIAL_PREVIEW + materialItems[0];
                                                material['img'] = img;
                                            }
                                            // END
                                            material['active'] = materialIndex === roomItem.defaultMaterial || false;
                                        }
                                        roomMaterial.push(material);
                                    }
                                    roomItemList.push({
                                        'id': roomItem.id,
                                        'name': roomItem.name,
                                        'materials': roomMaterial,
                                        'type': roomItem.type,
                                        'visible': roomItem.isActive,
                                        'camera': roomItem.camera
                                    });
                                });

                                currentRoom.items = roomItemList;
                                configurationOptions.push(currentRoom);
                            });
                            
                            payload.json = json;

                            payload.materials = json.materials;
                            payload.configurationOptions = configurationOptions;

                            json.name = payload.widgetTitle;
                            json.disclaimer = payload.disclaimer || undefined;
                            json.showCopyright = payload.showCopyright !== undefined ? payload.showCopyright : true;

                            //Remove plot title
                            payload.title = '';

                            payload.json.items = {};
                            payload.json.rooms.forEach(room => {
                                room.items.forEach(item => {
                                    payload.json.items[item.id] = item.materials[item.defaultMaterial];
                                });
                            });
                            let index = 0;
                            let scenes = [];
                            payload.json.rooms.forEach((room, roomIndex) => {
                                scenes.push({
                                    'key': ('' + (roomIndex)).padStart(4, '0'),
                                    'id': ('' + (roomIndex)).padStart(4, '0'),
                                    'index': index++,
                                    'name': room.name,
                                    'path': '',
                                    'sceneSuffix': '',
                                    'stereo': false,
                                    'multires': false,
                                    'regexp': '',
                                    'preview': false,
                                    'camera': room.camera,
                                    'panoType': 'cubestrip'
                                });
                            });

                            this.canvasData.scenes = _.cloneDeep(scenes);

                            payload.type = 'conf-models';
                            payload.widgetType = 'conf-models';

                            if (this.isExportMediaEnabled && (payload.private && 'studioExport' in payload.private))
                                this.formUrl = payload.private.studioExport;

                            if (this.standalone) {
                                let result = await EventBus.$emit('setSidebarData', payload);
                                EventBus.$emit('updateLoadedWidgets', {
                                    'id': payload.type,
                                    'link': urlLink,
                                    'active': true,
                                    'widgetType': payload.widgetType
                                });
                            } else {
                                let result = await EventBus.$emit('updateSidebar', payload, false);
                            }
                            if (!this.isLoadingScene ) {
                                this.isLoadingScene = true;
                                this.loadScene(0);
                            }
                        });
                    }
                });
            }
        },
        watch: {
            async link(newLink, oldLink) {
                if (this.link && !this.link.includes('conf-tour-models') && oldLink) {
                    this.updateData();
                }
            },
            isLoadingScene(val) {
                if (!val) {
                    this.triggerAnimation(this.$refs.confTourModelsHolder);
                    EventBus.$emit('sceneLoaded');
                }    
            }
        
        }
    };
</script>

<style lang="scss">
    @import "@/assets/scss/_common.scss";
    @import "@/assets/scss/main.scss";
    @import "@/assets/scss/components/_sidebar.scss";
</style>



// WEBPACK FOOTER //
// src/views/ConfTourModelsWidget.vue